import { useState } from 'react';
import { ArrowUpRight, ChevronDown, ExternalLink } from 'lucide-react';
import { COLLECTIONS } from '@/lib/taxonomy';
import { ORDISCAN_URL } from '@/lib/siteConfig';
import { cn } from '@/lib/utils';

/** How many inscriptions show before the reader asks for the rest. */
const PREVIEW_COUNT = 12;

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/** An inscription's page on the Ordiscan explorer. Ids are digits only. */
function inscriptionUrl(id: string): string {
  return `https://ordiscan.com/inscription/${encodeURIComponent(id)}`;
}

/**
 * The Archive & Directory manifest and every inscription it lists, shown on the
 * Bitcoin Inscriptions page. The data lives in taxonomy.ts (the Bitcoin
 * collection's `featuredLinks`, the entry marked `isManifest`), so adding an
 * inscription there adds it here and in the Directory's popup.
 */
export function InscriptionDirectory() {
  const [expanded, setExpanded] = useState(false);

  const bitcoin = COLLECTIONS.find((c) => c.id === 'bitcoin');
  const manifest = bitcoin?.featuredLinks?.find((l) => l.isManifest);
  const items = manifest?.inscriptions ?? [];

  if (!manifest || items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const hiddenCount = items.length - visible.length;

  return (
    <section aria-labelledby="inscription-directory-title" className="px-4 pb-6">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-2xl border border-foreground/30 border-t-[3px] border-t-ink bg-ivory p-4 shadow-[0_4px_10px_-6px_rgba(40,25,10,0.45)] sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0">
              <p className="font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-oxblood">
                Archive &amp; Directory
              </p>
              <h2
                id="inscription-directory-title"
                className="mt-1 font-display text-2xl font-bold leading-tight text-foreground sm:text-3xl"
              >
                Inscription directory
              </h2>
            </div>

            <a
              href={ORDISCAN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex shrink-0 items-center gap-2 self-start rounded-lg border border-flag-red px-4 py-2 font-engraved text-xs font-semibold uppercase tracking-[0.16em] text-flag-red transition-colors hover:bg-flag-red hover:text-background',
                FOCUS,
              )}
            >
              View on Ordiscan Explorer
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
            </a>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/80">
            The master manifest lists the records inscribed on Bitcoin. Each entry opens its
            inscription on Ordiscan.
          </p>

          {/* The manifest itself: the inscription that indexes the rest. */}
          <a
            href={manifest.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'mt-4 flex items-center justify-between gap-3 rounded-xl border border-flag-red bg-flag-red/[0.06] p-3 text-[14px] leading-snug text-foreground transition-colors hover:bg-flag-red/[0.12]',
              FOCUS,
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-flag-red">
                Master manifest
              </span>
              <span className="mt-0.5 block">{manifest.label}</span>
            </span>
            <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 text-flag-red" />
          </a>

          <p className="mt-5 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70">
            Listed inscriptions
            <span className="ml-2 font-mono tracking-normal text-foreground/60">{items.length}</span>
          </p>

          <ol
            id="inscription-directory-list"
            className="mt-2 divide-y divide-foreground/15 border-y border-foreground/25"
          >
            {visible.map((inscription, idx) => (
              <li key={inscription.id}>
                <a
                  href={inscriptionUrl(inscription.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'group flex items-start gap-3 px-1 py-2.5 transition-colors hover:bg-ink/[0.06]',
                    FOCUS,
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="w-7 shrink-0 pt-0.5 text-right font-mono text-[11px] tabular-nums text-foreground/50"
                  >
                    {idx + 1}
                  </span>
                  <span className="min-w-0 flex-1 text-[14px] leading-snug text-foreground/90 group-hover:text-foreground">
                    {inscription.title}
                  </span>
                  <span className="shrink-0 pt-0.5 font-mono text-[11px] text-foreground/60 group-hover:text-foreground">
                    #{inscription.id}
                  </span>
                  <ArrowUpRight
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink/60 group-hover:text-ink"
                  />
                </a>
              </li>
            ))}
          </ol>

          {items.length > PREVIEW_COUNT && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                aria-expanded={expanded}
                aria-controls="inscription-directory-list"
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-full border border-foreground/40 px-5 font-engraved text-[11px] font-bold uppercase tracking-[0.18em] text-foreground transition-colors hover:bg-foreground hover:text-background',
                  FOCUS,
                )}
              >
                {expanded ? 'Show fewer' : `Show all ${items.length}`}
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    'h-4 w-4 transition-transform motion-reduce:transition-none',
                    expanded && 'rotate-180',
                  )}
                />
                <span className="sr-only">
                  {expanded ? '' : `(${hiddenCount} more)`}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
